import React, { useEffect, useState } from "react";
import {
  Form,
  FormFeedback,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { resolveOverlappingPayments } from "common/services/payments.service";
import { toast } from "react-toastify";
import {
  OVERLAP_STATUS,
  OVERLAP_STATUS_OPTIONS,
} from "common/constants/overlap-status";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import OverlapStatusCards from "./OverlapStatusCards";
import {
  describeResolveOutcomes,
  resolveErrorMessage,
} from "./resolution";

/**
 * Double entry merges payments, which is too destructive to apply to a whole
 * selection at once, so it is only offered when resolving pairs one by one.
 */
const BULK_EXCLUDED_STATUSES: string[] = [
  OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY,
];

const BULK_STATUS_VALUES = OVERLAP_STATUS_OPTIONS.map(
  (option) => option.value
).filter((value) => !BULK_EXCLUDED_STATUSES.includes(value));

/**
 * Applies one status + one set of comments to many overlapping payments.
 *
 * "resolve" only ever targets unresolved records and "edit" only ever targets
 * already-resolved ones; the ineligible half of the selection is filtered out
 * by the caller and reported here as a skip count so the user can see why the
 * number of affected rows is smaller than what they ticked.
 */
const BulkResolvePayment: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  fetchData: () => void;
  eligibleIds: string[];
  skippedCount: number;
  mode: "resolve" | "edit";
  onCompleted: () => void;
}> = ({
  toggle,
  isModalOpen,
  fetchData,
  eligibleIds,
  skippedCount,
  mode,
  onCompleted,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const count = eligibleIds.length;
  const isEdit = mode === "edit";
  const plural = count === 1 ? "" : "s";

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      comments: "",
      overlapStatus: "",
    },
    validationSchema: Yup.object({
      comments: Yup.string(),
      overlapStatus: Yup.string()
        .oneOf(BULK_STATUS_VALUES, "Please select a valid status")
        .required("Please select a status"),
    }),
    onSubmit: async (values: any) => {
      if (!count) return;

      setLoading(true);
      try {
        const response: any = await resolveOverlappingPayments({
          ids: eligibleIds,
          overlapStatus: values.overlapStatus,
          comments: values.comments,
        });
        const { failed, message } = describeResolveOutcomes(
          response?.outcomes || []
        );
        // Some records may have changed even when others failed.
        fetchData();
        if (failed) {
          toast.error(message);
          return;
        }
        onCompleted();
        validation.resetForm();
        toggle();
        toast.success(message);
      } catch (e: any) {
        toast.error(
          resolveErrorMessage(
            e,
            isEdit
              ? "Bulk resolution update failed"
              : "Bulk payment resolution failed"
          )
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // Values are shared across both modes, so start from a clean form each time
  // rather than carrying the previous action's status/comments over.
  useEffect(() => {
    if (isModalOpen) {
      validation.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, mode]);

  return (
    <Modal
      id="bulkResolveModal"
      size="lg"
      isOpen={isModalOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        {isEdit
          ? `Edit Resolution for ${count} Payment${plural}`
          : `Resolve ${count} Duplicate Payment${plural}`}
      </ModalHeader>

      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          <p className="text-muted">
            This status and these comments will {isEdit ? "replace the " : ""}
            {isEdit ? "existing resolution on" : "be applied to"} all {count}{" "}
            selected payment{plural}.
          </p>

          {skippedCount > 0 && (
            <div className="alert alert-warning" role="alert">
              <i className="ri-information-line align-bottom me-1"></i>
              {skippedCount} selected payment{skippedCount === 1 ? "" : "s"}{" "}
              {skippedCount === 1 ? "is" : "are"}{" "}
              {isEdit ? "not resolved yet" : "already resolved"} and will be
              skipped.
            </div>
          )}

          <Row>
            <Col md={12} className="mb-4">
              <div id="bulkOverlapStatus-label" className="form-label">
                What is this payment?
              </div>
              <OverlapStatusCards
                name="overlapStatus"
                idPrefix="bulkOverlapStatus"
                exclude={BULK_EXCLUDED_STATUSES}
                labelledBy="bulkOverlapStatus-label"
                value={validation.values.overlapStatus}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.overlapStatus &&
                  !!validation.errors.overlapStatus
                }
              />
              {validation.touched.overlapStatus &&
              validation.errors.overlapStatus ? (
                // FormFeedback only shows next to an invalid <input>, which
                // the cards are not, so force it visible.
                <div className="invalid-feedback d-block">
                  {/* @ts-ignore */}
                  {validation.errors.overlapStatus}
                </div>
              ) : null}
            </Col>
            <Col md={12} className="mb-3">
              <Label htmlFor="bulk-comments-field" className="form-label">
                Comments on resolution (Optional)
              </Label>
              <CKEditor
                editor={ClassicEditor}
                data={validation.values.comments || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  validation.setFieldValue("comments", data);
                }}
                onBlur={() => {
                  validation.handleBlur("comments");
                }}
              />
              {validation.touched.comments && validation.errors.comments ? (
                <FormFeedback type="invalid">
                  {/* @ts-ignore */}
                  {validation.errors?.comments}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
        </ModalBody>

        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                toggle();
              }}
            >
              Close
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || count === 0}
            >
              {loading && (
                <Spinner
                  color="light"
                  size="sm"
                  style={{ marginRight: "8px", marginBottom: "-1px" }}
                ></Spinner>
              )}
              {isEdit
                ? `Update ${count} Resolution${plural}`
                : `Resolve ${count} Payment${plural}`}
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default BulkResolvePayment;
